import {
  type adminLightStufen,
  editionVerkaufbar,
  FUNKTIONSNAMEN,
  funktionen,
  type LizenzVorschau,
  type PartnerEdition,
  type PartnerKunde,
  type PartnerKundeAenderung,
  type PartnerRecht,
} from '@nac2s/shared'
import { useEffect, useState } from 'react'
import { Label } from '@/components/base/input/label'
import { Select } from '@/components/base/select/select'
import { Toggle } from '@/components/base/toggle/toggle'
import { Dialogfenster } from '@/teile/Dialogfenster'
import { Knopf } from '@/teile/Knopf'
import { type Merkmal, Merkmalsliste } from '@/teile/Merkmalsliste'
import { Fehler } from '@/teile/Tabelle'
import { cx } from '@/utils/cx'
import { ApiError } from '../api/client.js'
import {
  kundeAendern,
  ladeAenderungen,
  ladeEditionen,
  lizenzKaufen,
  lizenzVorschau,
} from '../api/partner.js'
import { sperrsatz } from '../pages/Katalog.js'

/**
 * Die Stellschrauben eines Kunden.
 *
 * **Nicht alles darf jeder.** Seeds-Obergrenze und Edition sind Preisschilder
 * und gehören dem Vertrieb, Gerätelimit und Selbstverwaltung der Technik. Was
 * ein Konto nicht ändern darf, wird **gezeigt und gesperrt** statt versteckt:
 * Ein Techniker, der die gebuchte Edition nicht sieht, ruft an und fragt
 * danach. Warum etwas grau ist, steht **einmal** unter den Feldern und nicht
 * unter jedem einzelnen.
 *
 * Durchgesetzt wird die Trennung am Endpunkt, nicht hier (`403`). Diese Seite
 * spart nur den Weg dorthin.
 *
 * **Schmal untereinander, und rechts steht, was die Edition bringt.** Die
 * Auswahl klappt so breit auf, wie sie geschlossen ist — nebeneinander in
 * einer Zeile blieben je 13 rem, und darin ließ sich nicht lesen, wofür man
 * sich entscheidet. Breiter als nötig müssen die Felder deshalb nicht sein:
 * Was eine Edition mitbringt, gehört nicht ins Feld, sondern daneben.
 *
 * Geändert wird erst beim Speichern, nicht beim Tippen. Ein Feld, das beim
 * Verlassen speichert, erzeugt Änderungen, die niemand wollte — und jede
 * davon steht danach im Nachweis.
 *
 * **Die Selbstverwaltung ist eine Obergrenze, keine Vergabe.** Sie sagt, was
 * bei diesem Kunden überhaupt möglich ist (`Projektstruktur.md` §3.1) — wer es
 * dort tatsächlich darf, steht am einzelnen Anwender im Reiter „Anwender".
 * Beides gehört dem Partner: erst der Rahmen, dann die Person.
 */

/**
 * Die Editionen kommen aus dem Katalog, den netable diesem Partner gegeben
 * hat (ADR-0014): Name, Funktionen, sein Preis je Seed, ein Ende. Nichts
 * davon steht hier fest. Wer eine Edition waehlt und speichert, **kauft** sie
 * fuer diesen Kunden — abgerechnet wird Preis mal Seeds, monatlich.
 */
function euro(cent: number): string {
  return (cent / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
}

function heuteTag(): string {
  return new Date().toISOString().slice(0, 10)
}

function datum(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/** Alle Funktionen, die es gibt — mit Haken, was die Edition traegt. */
function merkmaleVon(katalog: PartnerEdition[], edition: string): Merkmal[] {
  const traegt = new Set(katalog.find((e) => e.key === edition)?.funktionen ?? [])
  return funktionen.map((f) => ({ name: FUNKTIONSNAMEN[f].name, enthalten: traegt.has(f) }))
}

const SELBSTVERWALTUNG = [
  { id: 'keine', label: 'Keine', supportingText: 'Alles läuft über Sie' },
  { id: 'geraete', label: 'Geräte', supportingText: 'Kopplungscodes ausgeben, Geräte sperren' },
  {
    id: 'anwender_und_geraete',
    label: 'Anwender und Geräte',
    supportingText: 'Zusätzlich Anwender anlegen und deaktivieren',
  },
]

const GERAETEWAHL = Array.from({ length: 10 }, (_, i) => ({
  id: String(i + 1),
  label: `${i + 1} ${i === 0 ? 'Gerät' : 'Geräte'}`,
}))

interface Entwurf {
  seatCap: string
  adminLightStufe: string
  geraeteJeAnwender: string
  zweiFaktorPflicht: string
}

function ausKunde(kunde: PartnerKunde): Entwurf {
  return {
    seatCap: kunde.seatCap === null ? '' : String(kunde.seatCap),
    adminLightStufe: kunde.adminLightStufe,
    geraeteJeAnwender: String(kunde.geraeteJeAnwender),
    zweiFaktorPflicht: kunde.zweiFaktorPflicht ? 'ja' : 'nein',
  }
}

export function Kundeneinstellungen({
  kunde,
  rechte,
  onGeaendert,
}: {
  kunde: PartnerKunde
  rechte: readonly PartnerRecht[]
  onGeaendert: () => void
}) {
  const [entwurf, setEntwurf] = useState<Entwurf>(() => ausKunde(kunde))
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [nachweis, setNachweis] = useState<PartnerKundeAenderung[]>([])
  const [katalog, setKatalog] = useState<PartnerEdition[]>([])
  const [kaufOffen, setKaufOffen] = useState(false)

  useEffect(() => {
    let weg = false
    ladeEditionen()
      .then((k) => {
        if (!weg) setKatalog(k.editionen)
      })
      .catch(() => {
        // Ohne Katalog bleibt die Edition so, wie sie ist — die Wahl ist dann leer.
      })
    return () => {
      weg = true
    }
  }, [])

  const darfGeld = rechte.includes('kunden.kaufmaennisch')
  const darfTechnik = rechte.includes('kunden.technik')
  const ausgangslage = ausKunde(kunde)
  const geaendert = (Object.keys(entwurf) as (keyof Entwurf)[]).some(
    (k) => entwurf[k] !== ausgangslage[k],
  )

  // Der Entwurf folgt dem Kunden, wenn der neu geladen wurde — sonst zeigte
  // das Formular nach dem Speichern noch die alten Werte als „geändert".
  useEffect(() => setEntwurf(ausKunde(kunde)), [kunde])

  useEffect(() => {
    let weg = false
    ladeAenderungen(kunde.host)
      .then((a) => {
        if (!weg) setNachweis(a)
      })
      .catch(() => {
        // Der Nachweis ist Beiwerk. Faellt er aus, bleibt die Seite benutzbar.
      })
    return () => {
      weg = true
    }
  }, [kunde.host])

  async function speichern() {
    setLaeuft(true)
    setMeldung(null)
    try {
      await kundeAendern(kunde.host, {
        ...(darfGeld
          ? {
              seatCap: entwurf.seatCap.trim() === '' ? null : Number(entwurf.seatCap),
            }
          : {}),
        ...(darfTechnik
          ? {
              adminLightStufe: entwurf.adminLightStufe as (typeof adminLightStufen)[number],
              geraeteJeAnwender: Number(entwurf.geraeteJeAnwender),
              zweiFaktorPflicht: entwurf.zweiFaktorPflicht === 'ja',
            }
          : {}),
      })
      onGeaendert()
      setNachweis(await ladeAenderungen(kunde.host))
    } catch (fehler: unknown) {
      setMeldung(satzZu(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start gap-x-8 gap-y-5">
        <div className="flex w-[17rem] shrink-0 flex-col gap-3.5">
          <Feld
            name="Seeds-Obergrenze"
            disabled={!darfGeld}
            wert={entwurf.seatCap}
            onWert={(w) => setEntwurf((v) => ({ ...v, seatCap: w }))}
          />

          <LizenzBlock
            kunde={kunde}
            katalog={katalog}
            darfGeld={darfGeld}
            onKaufen={() => setKaufOffen(true)}
          />

          <Wahl
            name="Selbstverwaltung"
            items={SELBSTVERWALTUNG}
            wert={entwurf.adminLightStufe}
            disabled={!darfTechnik}
            onWahl={(w) => setEntwurf((v) => ({ ...v, adminLightStufe: w }))}
          />

          <Wahl
            name="Geräte je Anwender"
            items={GERAETEWAHL}
            wert={entwurf.geraeteJeAnwender}
            disabled={!darfTechnik}
            onWahl={(w) => setEntwurf((v) => ({ ...v, geraeteJeAnwender: w }))}
          />

          <Schalter
            name="Zweiten Faktor für alle erzwingen"
            an={entwurf.zweiFaktorPflicht === 'ja'}
            disabled={!darfTechnik}
            onAn={(an) => setEntwurf((v) => ({ ...v, zweiFaktorPflicht: an ? 'ja' : 'nein' }))}
          />

          <Gesperrt darfGeld={darfGeld} darfTechnik={darfTechnik} />

          <div className="flex items-center gap-2.5 pt-0.5">
            {geaendert ? (
              <Knopf onClick={() => setEntwurf(ausgangslage)} disabled={laeuft}>
                Verwerfen
              </Knopf>
            ) : null}
            <Knopf stark onClick={() => void speichern()} disabled={!geaendert || laeuft}>
              {laeuft ? 'Wird gespeichert …' : 'Speichern'}
            </Knopf>
          </div>
        </div>

        <div className="w-[19rem] shrink-0">
          <Merkmalsliste
            titel="Was diese Edition bringt"
            merkmale={merkmaleVon(katalog, kunde.edition)}
          />
        </div>
      </div>

      {kunde.seatCap !== null && kunde.seatsBelegt !== null && kunde.seatsBelegt > kunde.seatCap ? (
        <p className="rounded-xl bg-warning-primary p-3 text-[13px] text-warning-primary">
          Die Obergrenze liegt unter den belegten Seeds ({kunde.seatsBelegt}). Vorhandene Anwender
          bleiben; neue lassen sich nicht mehr anlegen.
        </p>
      ) : null}

      {meldung ? <Fehler>{meldung}</Fehler> : null}

      <Nachweis eintraege={nachweis} editionsnamen={new Map(katalog.map((e) => [e.key, e.name]))} />
      {kaufOffen ? (
        <KaufDialog
          kunde={kunde}
          katalog={katalog}
          onAbbruch={() => setKaufOffen(false)}
          onGekauft={() => {
            setKaufOffen(false)
            onGeaendert()
            ladeAenderungen(kunde.host)
              .then(setNachweis)
              .catch(() => {})
          }}
        />
      ) : null}
    </div>
  )
}

/**
 * Die Lizenz des Kunden (ADR-0015): welche Edition, welche Laufzeit, welche
 * Mindestmenge, bis wann. Aendern heisst kaufen — ueber den Dialog, nicht
 * ueber ein Feld: Der Kauf setzt Start und Laufzeit, und das soll niemand
 * nebenbei tun.
 */
function LizenzBlock({
  kunde,
  katalog,
  darfGeld,
  onKaufen,
}: {
  kunde: PartnerKunde
  katalog: PartnerEdition[]
  darfGeld: boolean
  onKaufen: () => void
}) {
  const edition = katalog.find((e) => e.key === kunde.edition)
  const l = kunde.lizenz
  const endeBald =
    l !== null && new Date(`${l.laufzeitEnde}T00:00:00`).getTime() - Date.now() < 31 * 86_400_000
  return (
    <div className="flex flex-col gap-1.5">
      <Label>Edition und Lizenz</Label>
      <div className="rounded-xl border border-secondary p-3 text-[13px]">
        <p className="font-semibold text-primary">{edition?.name ?? kunde.edition}</p>
        {l ? (
          <>
            <p className="mt-0.5 text-tertiary">
              {l.laufzeit === 'monatlich'
                ? 'Monatliche Laufzeit'
                : `Mindestlaufzeit ${l.mindestlaufzeitMonate} Monate`}
              {` · mindestens ${l.mindestmenge} Seeds`}
            </p>
            {l.bezahltAb > heuteTag() ? (
              <p className="mt-0.5 text-akzent-tief tabular-nums">
                Testzeitraum bis {datum(l.bezahltAb)} — berechnet wird ab dann.
              </p>
            ) : null}
            <p
              className={cx(
                'mt-0.5 tabular-nums',
                endeBald ? 'text-achtung-tief' : 'text-quaternary',
              )}
            >
              seit {datum(l.start)} ·{' '}
              {l.laufzeit === 'monatlich' ? 'kündbar zum' : 'verlängert sich am'}{' '}
              {datum(l.laufzeitEnde)}
              {endeBald && l.laufzeit === 'mindest' ? ' — um zwölf Monate' : ''}
            </p>
          </>
        ) : (
          <p className="mt-0.5 text-tertiary">Noch keine Lizenz gekauft.</p>
        )}
        <div className="mt-2.5">
          <Knopf disabled={!darfGeld} onClick={onKaufen}>
            {l ? 'Edition wechseln' : 'Edition kaufen'}
          </Knopf>
        </div>
      </div>
    </div>
  )
}

/**
 * Kaufen: Edition aus dem Katalog, Laufzeit, Mindestmenge. Der Preis steht
 * daneben, bevor jemand kauft. Waehrend einer Mindestlaufzeit geht nur ein
 * Aufstieg — der Server sagt sonst `laufzeit_laeuft`.
 */
function KaufDialog({
  kunde,
  katalog,
  onAbbruch,
  onGekauft,
}: {
  kunde: PartnerKunde
  katalog: PartnerEdition[]
  onAbbruch: () => void
  onGekauft: () => void
}) {
  const wahl = katalog.filter((e) => editionVerkaufbar(e))
  const [edition, setEdition] = useState(
    wahl.some((e) => e.key === kunde.edition) ? kunde.edition : (wahl[0]?.key ?? ''),
  )
  const [laufzeit, setLaufzeit] = useState<'mindest' | 'monatlich'>(
    kunde.lizenz?.laufzeit ?? 'mindest',
  )
  const [mindestmenge, setMindestmenge] = useState(String(kunde.lizenz?.mindestmenge ?? 0))
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [vorschau, setVorschau] = useState<LizenzVorschau | null>(null)

  const ziel = wahl.find((e) => e.key === edition)
  const menge = /^\d{1,6}$/.test(mindestmenge.trim()) ? Number(mindestmenge) : null

  // Preis, Testzeitraum und Erlaubnis rechnet der Server — die Oberflaeche zeigt nur an.
  const zielKey = ziel?.key ?? null
  const host = kunde.host
  useEffect(() => {
    if (!zielKey || menge === null) {
      setVorschau(null)
      return
    }
    let aktuell = true
    const t = setTimeout(() => {
      lizenzVorschau(host, { edition: zielKey, laufzeit, mindestmenge: menge })
        .then((v) => {
          if (aktuell) setVorschau(v)
        })
        .catch(() => {
          if (aktuell) setVorschau(null)
        })
    }, 250)
    return () => {
      aktuell = false
      clearTimeout(t)
    }
  }, [host, zielKey, laufzeit, menge])
  const sperre = vorschau ? sperrsatz(kunde.anzeigename, vorschau) : null

  async function kaufen() {
    if (!ziel || menge === null) return
    setLaeuft(true)
    setMeldung(null)
    try {
      await lizenzKaufen(kunde.host, { edition: ziel.key, laufzeit, mindestmenge: menge })
      onGekauft()
    } catch (fehler: unknown) {
      setMeldung(
        fehler instanceof ApiError && fehler.code === 'laufzeit_laeuft'
          ? 'Die Mindestlaufzeit läuft noch: Bis zu ihrem Ende geht nur ein Aufstieg — mehr Mindestmenge oder eine Edition mit mehr Funktionen, keine monatliche Laufzeit.'
          : fehler instanceof ApiError && fehler.code === 'edition_nicht_verfuegbar'
            ? 'Diese Edition steht nicht mehr in Ihrem Katalog.'
            : satzZu(fehler),
      )
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <Dialogfenster
      titel={kunde.lizenz ? 'Edition wechseln' : 'Edition kaufen'}
      satz="Berechnet wird monatlich der höhere Wert aus Seeds und Mindestmenge — ab dem Ende des Testzeitraums."
      onSchliessen={onAbbruch}
    >
      <div className="flex flex-col gap-4 pb-6">
        {wahl.length === 0 ? (
          <Fehler>Ihr Katalog enthält keine verkaufbare Edition.</Fehler>
        ) : (
          <>
            <Wahl
              name="Edition"
              items={wahl.map((e) => ({
                id: e.key,
                label: e.name,
                supportingText:
                  e.seedCent === null
                    ? 'Preis noch nicht hinterlegt'
                    : `${euro(e.seedCent)} je Seed und Monat`,
              }))}
              wert={edition}
              disabled={false}
              onWahl={setEdition}
            />
            <Wahl
              name="Laufzeit"
              items={[
                {
                  id: 'mindest',
                  label:
                    ziel === undefined
                      ? 'Jährlich'
                      : `Jährlich — Mindestlaufzeit ${ziel.mindestlaufzeitMonate} Monate`,
                  supportingText: `${ziel?.seedCent === null || ziel === undefined ? '' : `${euro(ziel.seedCent)} je Seed und Monat, −${ziel.monatlichAufschlagProzent} %. `}Verlängert sich danach um je zwölf Monate.`,
                },
                {
                  id: 'monatlich',
                  label: 'Monatlich',
                  supportingText: `${ziel?.seedMonatlichCent === null || ziel === undefined ? '' : `${euro(ziel.seedMonatlichCent)} je Seed und Monat. `}Seeds zum Monatsende kündbar.`,
                },
              ]}
              wert={laufzeit}
              disabled={false}
              onWahl={(w) => setLaufzeit(w === 'monatlich' ? 'monatlich' : 'mindest')}
            />
            <Feld
              name="Mindestmenge Seeds"
              disabled={false}
              wert={mindestmenge}
              onWert={setMindestmenge}
            />
            {ziel ? (
              <Merkmalsliste
                titel="Was diese Edition bringt"
                merkmale={funktionen.map((f) => ({
                  name: FUNKTIONSNAMEN[f].name,
                  enthalten: ziel.funktionen.includes(f),
                }))}
              />
            ) : null}
            {vorschau ? (
              <p className="rounded-xl bg-akzent-flaeche p-3 text-[12.5px] text-akzent-tief">
                {vorschau.imTestzeitraum
                  ? `Testzeitraum bis ${datum(vorschau.bezahltAb)}, danach: `
                  : 'Die laufende Lizenz wird fortgesetzt, der Wechsel gilt ab sofort: '}
                {vorschau.preisJeSeedCent === null || vorschau.gesamtJeMonatCent === null
                  ? 'Ein Preis ist für diese Edition noch nicht hinterlegt.'
                  : `${euro(vorschau.preisJeSeedCent)} je Seed und Monat, netto${vorschau.laufzeit === 'monatlich' ? ' (monatlich kündbar)' : ` (jährlich −${vorschau.monatlichAufschlagProzent} %)`}. Bei ${vorschau.mindestmenge} Seeds mindestens ${euro(vorschau.gesamtJeMonatCent)} je Monat, fällig ab ${datum(vorschau.bezahltAb)}.`}
              </p>
            ) : (
              <p className="text-[12.5px] text-tertiary">Wird berechnet …</p>
            )}
            {sperre ? <Fehler>{sperre}</Fehler> : null}
          </>
        )}
        {meldung ? <Fehler>{meldung}</Fehler> : null}
        <div className="flex justify-end gap-2">
          <Knopf onClick={onAbbruch} disabled={laeuft}>
            Abbrechen
          </Knopf>
          <Knopf
            stark
            disabled={laeuft || !ziel || menge === null || !vorschau || sperre !== null}
            onClick={() => void kaufen()}
          >
            {kunde.lizenz ? 'Wechseln' : 'Kaufen'}
          </Knopf>
        </div>
      </div>
    </Dialogfenster>
  )
}

/**
 * Warum etwas grau ist — **einmal**, nicht unter jedem Feld.
 *
 * Ohne diesen Satz stünde ein Techniker vor zwei gesperrten Feldern und wüsste
 * nicht, ob sie kaputt sind oder ihm nicht gehören.
 */
function Gesperrt({ darfGeld, darfTechnik }: { darfGeld: boolean; darfTechnik: boolean }) {
  if (darfGeld && darfTechnik) return null

  return (
    <p className="text-[11.5px] text-quaternary">
      {darfGeld
        ? 'Selbstverwaltung und Geräte je Anwender ändern nur Technik und Admin.'
        : 'Seeds-Obergrenze und Edition ändern nur Vertrieb und Admin.'}
    </p>
  )
}

/**
 * Zahlenfeld.
 *
 * Die Beschriftung ist **dasselbe Bauteil** wie über den Auswahlfeldern
 * (`Label` von Untitled UI). Vorher war sie hier handgesetzt, eine Spur
 * kleiner und heller — vier Felder untereinander, und eines davon sah aus wie
 * von einer anderen Seite.
 *
 * Der Bezug laeuft ueber `htmlFor` und `id` und nicht ueber Umschliessen: Ein
 * `<label>`, das sein Feld als `children` bekommt, ist fuer den Browser zwar
 * gueltig, aber fuer jeden Pruefer — und fuer den Naechsten, der es liest —
 * nicht nachvollziehbar.
 */
function Feld({
  name,
  disabled,
  wert,
  onWert,
}: {
  name: string
  disabled?: boolean
  wert: string
  onWert: (wert: string) => void
}) {
  const id = `feld-${name.replace(/\W+/g, '-').toLowerCase()}`
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{name}</Label>
      <input
        id={id}
        type="number"
        min={0}
        inputMode="numeric"
        value={wert}
        disabled={disabled ?? false}
        // Der Platzhalter sagt, was ein leeres Feld bedeutet. Als Zeile
        // darunter waere es ein Untertitel mehr, den niemand braucht, sobald
        // eine Zahl drinsteht.
        placeholder="kein Cap"
        onChange={(e) => onWert(e.target.value)}
        className="feld min-h-[38px] w-full rounded-[10px] bg-primary px-3 text-[13.5px] text-primary tabular-nums transition-[box-shadow] duration-[180ms] ease-out outline-none focus:feld-schein disabled:bg-secondary disabled:text-tertiary"
      />
    </div>
  )
}

/**
 * Auswahl mit Erklärung — **in der Liste, nicht im geschlossenen Feld**.
 *
 * Untitled UI zeigt im zugeklappten Zustand Name *und* Zusatztext. Nebeneinander
 * in einem schmalen Feld gewinnt der längere von beiden den Platz, und dann
 * steht dort „Click-to-Dial und Verlauf" statt „Standard" — die Antwort auf
 * eine Frage, die niemand gestellt hat.
 *
 * Behoben ohne Eingriff in die Quelle des Bauteils: Das geschlossene Feld
 * zeigt den Eintrag aus `items` — also geht dort **nur** `id` und `label`
 * hinein. Die Erklärung bekommt erst der Listeneintrag, und die Liste ist so
 * breit wie das Feld.
 *
 * Nachgeprüft, nicht vermutet: Ein `value` am `Select.Item` reicht nicht, die
 * Sammlung gewinnt.
 */
/**
 * Ein Schalter für eine Einstellung, die es nur an oder aus gibt.
 *
 * Der zweite Faktor für alle (ADR-0004 Nachtrag 7) ist genau das. Ein
 * Wahlfeld mit zwei Werten stellte etwas zur Wahl, was keine Wahl ist: Ein
 * Verwalter beim Kunden braucht den Faktor immer, unabhängig von diesem
 * Schalter. Hier geht es allein darum, ob er auch für alle übrigen gilt.
 */
function Schalter({
  name,
  an,
  disabled,
  onAn,
}: {
  name: string
  an: boolean
  disabled: boolean
  onAn: (an: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[11.5px] font-medium text-quaternary">{name}</span>
      <Toggle size="sm" aria-label={name} isSelected={an} isDisabled={disabled} onChange={onAn} />
    </div>
  )
}

function Wahl({
  name,
  items,
  wert,
  disabled,
  onWahl,
}: {
  name: string
  items: { id: string; label: string; supportingText?: string }[]
  wert: string
  disabled: boolean
  onWahl: (wert: string) => void
}) {
  const schlank = items.map(({ id, label }) => ({ id, label }))
  const erklaerung = new Map(items.map((i) => [i.id, i.supportingText]))

  return (
    <Select
      size="sm"
      label={name}
      items={schlank}
      selectedKey={wert}
      isDisabled={disabled}
      onSelectionChange={(k) => onWahl(String(k))}
    >
      {(eintrag) => {
        const text = erklaerung.get(String(eintrag.id))
        return <Select.Item {...eintrag} {...(text ? { supportingText: text } : {})} />
      }}
    </Select>
  )
}

/**
 * Wer hat wann was gedreht?
 *
 * Steht unter den Feldern und nicht in einem eigenen Reiter: Die Frage kommt
 * genau dann auf, wenn jemand auf einen Wert schaut und sich wundert.
 */
function Nachweis({
  eintraege,
  editionsnamen,
}: {
  eintraege: PartnerKundeAenderung[]
  editionsnamen: ReadonlyMap<string, string>
}) {
  if (eintraege.length === 0) {
    return (
      <p className="border-t border-secondary pt-4 text-[12.5px] text-quaternary">
        Noch nichts geändert.
      </p>
    )
  }

  return (
    <div className="border-t border-secondary pt-4">
      <p className="text-[11.5px] font-medium text-quaternary">Änderungen</p>
      <ul className="mt-2 flex flex-col gap-1.5">
        {eintraege.map((e) => (
          <li key={`${e.wann}-${e.feld}`} className="text-[12.5px] text-tertiary">
            <span className="tabular-nums">{zeitpunkt(e.wann)}</span>
            {' · '}
            <span className="text-primary">{FELDNAMEN[e.feld] ?? e.feld}</span>
            {': '}
            {wert(e.vorher, editionsnamen)} →{' '}
            <span className="text-primary">{wert(e.nachher, editionsnamen)}</span>
            {' · '}
            {e.wer}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Spaltennamen in Worte. Im Nachweis steht der Spaltenname — hier nicht. */
const FELDNAMEN: Record<string, string> = {
  seat_cap: 'Seeds-Obergrenze',
  edition: 'Edition',
  admin_light_level: 'Selbstverwaltung',
  device_limit_per_user: 'Geräte je Anwender',
}

function wert(roh: string | null, editionsnamen: ReadonlyMap<string, string>): string {
  if (roh === null) return 'kein Cap'
  return editionsnamen.get(roh) ?? SELBSTVERWALTUNG.find((s) => s.id === roh)?.label ?? roh
}

function zeitpunkt(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function satzZu(fehler: unknown): string {
  if (fehler instanceof ApiError) {
    if (fehler.status === 403) {
      return 'Ihre Rolle darf eines dieser Felder nicht ändern. Es wurde nichts gespeichert.'
    }
    if (fehler.code === 'kunde_geloescht')
      return 'Dieser Kunde ist gelöscht und lässt sich nicht mehr ändern.'
    return fehler.message
  }
  return 'Unbekannter Fehler'
}
